import imageCompression from "browser-image-compression";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { ImageCompressConfig } from "../../../../constants/ImageCompressConfig";
import { ApiStatusTypeEnum } from "../../../../enumeration/ApiStatusTypeEnum";
import { ErrorContainer, Input } from "../../../common/control/InputField";
import * as actions from "../../redux/CompanyGameAction";

const initValues = {
  companycode: "",
  gametype: "",
  category: "",
  gamenameen: "",
  gamenamecn: "",
  gamecode: "",
  gametag: "",
  image: undefined,
};

const schema = (intl, isImageRequired) => {
  return Yup.object().shape({
    gamenameen: Yup.string()
      .nullable(false)
      .required(intl.formatMessage({ id: "gameNameEnRequired" })),
    gamenamecn: Yup.string()
      .nullable(false)
      .required(intl.formatMessage({ id: "gameNameZhRequired" })),
    gamecode: Yup.string()
      .nullable(false)
      .required(intl.formatMessage({ id: "gameCodeRequired" })),
    ...(isImageRequired
      ? {
          image: Yup.mixed().required(
            intl.formatMessage({ id: "imageRequired" })
          ),
        }
      : {}),
  });
};

export const ManageGameForm = ({
  formikRef,
  saveValues,
  fileInputRef,
  show,
  imagePreview,
  setImagePreview,
  companycode,
  gametype,
  item,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [existGamenameen, setExistGamenameen] = useState("");

  const companyGameState = useSelector(
    ({ companyGame }) => companyGame,
    shallowEqual
  );

  useEffect(() => {
    return () => setExistGamenameen("");
  }, [show]);

  useEffect(() => {
    if (companyGameState.companyList && show) {
      setImagePreview(undefined);
      const selected = companyGameState.companyList.gameTypes[0];
      formikRef.current.setValues({
        ...initValues,
        companycode: companycode ?? selected.companylists[0].companycode,
        gametype: gametype ?? selected.gametype,
        category: companyGameState.companyList.gameCategories[0].code,
      });
    }
  }, [companyGameState.companyList, show, companycode, gametype]);

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    if (file.type !== "image/webp") {
      event.target.value = "";
      return;
    }

    const handleFile = (file, fileName) => {
      const newFile = new File([file], fileName, {
        type: file.type,
      });
      setImagePreview(URL.createObjectURL(newFile));
      formikRef.current?.setFieldValue("image", newFile);
    };

    const fileName = file.name;
    imageCompression(file, ImageCompressConfig)
      .then((res) => handleFile(res, fileName))
      .catch(() => handleFile(file, fileName));
  };

  const handleCheckGamename = (gamecode, companycode, gametype) => {
    dispatch(
      actions.checkGameCodeExist({ gamecode, companycode, gametype })
    ).then((resp) => {
      resp = resp.data;
      if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
        setExistGamenameen(resp.gamenameen);
      }
    });
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initValues}
      enableReinitialize={true}
      validationSchema={schema(intl, !item)}
      onSubmit={(values) => {
        saveValues(values);
      }}
    >
      {({
        values,
        handleSubmit,
        setFieldValue,
        touched,
        errors,
        setFieldTouched,
      }) => (
        <Form>
          {!item && (
            <>
              <div className="mb-2">
                <label className="mb-2 text-input-label">
                  {intl.formatMessage({ id: "gameType" })}
                </label>
                <select
                  className="form-select"
                  name="gametype"
                  value={values.gametype}
                  onChange={(e) => {
                    setFieldValue("gametype", e.target.value);
                    setFieldValue(
                      "companycode",
                      companyGameState.companyList?.gameTypes?.find(
                        (gameType) => gameType.gametype === e.target.value
                      )?.companylists[0].companycode
                    );
                  }}
                >
                  {companyGameState.companyList?.gameTypes?.map((gameType) => (
                    <option value={gameType.gametype} key={gameType.gametype}>
                      {gameType.gametype}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="mb-2 text-input-label">
                  {intl.formatMessage({ id: "company" })}
                </label>
                <select
                  className="form-select"
                  name="companycode"
                  value={values.companycode}
                  onChange={(e) => setFieldValue("companycode", e.target.value)}
                >
                  {companyGameState.companyList?.gameTypes
                    ?.find((gameType) => gameType.gametype === values.gametype)
                    ?.companylists.map((company) => (
                      <option
                        value={company.companycode}
                        key={company.companycode}
                      >
                        {company.companyname}
                      </option>
                    ))}
                </select>
              </div>
            </>
          )}
          <div className="mb-2">
            <label className="mb-2 text-input-label">
              {intl.formatMessage({ id: "category" })}(
              {intl.formatMessage({ id: "optional" })})
            </label>
            <select
              className="form-select"
              name="category"
              value={values.category}
              onChange={(e) => setFieldValue("category", e.target.value)}
            >
              {companyGameState.companyList?.gameCategories?.map((category) => (
                <option value={category.code} key={category.code}>
                  {category.description}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-2">
            <label className="mb-2 text-input-label">
              {intl.formatMessage({ id: "gameName" })}(en)
            </label>
            <Field
              name="gamenameen"
              component={Input}
              placeholder={`${intl.formatMessage({ id: "gameName" })}(en)`}
              autoComplete="off"
              type="text"
              inputClass="form-control"
              isAlphanumericOnly={false}
            />
          </div>
          <div className="mb-2">
            <label className="mb-2 text-input-label">
              {intl.formatMessage({ id: "gameName" })}(zh)
            </label>
            <Field
              name="gamenamecn"
              component={Input}
              placeholder={`${intl.formatMessage({ id: "gameName" })}(zh)`}
              autoComplete="off"
              type="text"
              inputClass="form-control"
              isAlphanumericOnly={false}
            />
          </div>
          <div className="mb-2">
            <label className="mb-2 text-input-label">
              {intl.formatMessage({ id: "gameCode" })}
            </label>
            <div className="d-flex">
              <Field
                name="gamecode"
                component={Input}
                placeholder={intl.formatMessage({ id: "gameCode" })}
                autoComplete="off"
                type="text"
                inputClass="form-control flex-fill"
                isAlphanumericOnly={false}
              />
              <button
                type="button"
                className="btn btn-primary ms-2"
                onClick={() =>
                  handleCheckGamename(
                    values.gamecode,
                    values.companycode,
                    values.gametype
                  )
                }
              >
                {intl.formatMessage({ id: "check" })}
              </button>
            </div>
            {existGamenameen && (
              <div className="text-danger">
                {intl.formatMessage({ id: "existGameName" })} :{" "}
                {existGamenameen}
              </div>
            )}
          </div>
          <div className="mb-2">
            <label className="mb-2 text-input-label">
              {intl.formatMessage({ id: "gameTag" })}(
              {intl.formatMessage({ id: "optional" })})
            </label>
            <Field
              name="gametag"
              component={Input}
              placeholder={intl.formatMessage({ id: "gameTag" })}
              autoComplete="off"
              type="text"
              inputClass="form-control"
              isAlphanumericOnly={false}
            />
          </div>
          {item ? (
            <div className="mb-2">
              <label className="mb-2 text-input-label">
                {intl.formatMessage({ id: "status" })}
              </label>
              <select
                className="form-select"
                name="gamestatus"
                value={values.gamestatus}
                onChange={(e) => setFieldValue("gamestatus", e.target.value)}
              >
                <option value="available">
                  {intl.formatMessage({ id: "available" })}
                </option>
                <option value="onhold">
                  {intl.formatMessage({ id: "onHold" })}
                </option>
              </select>
            </div>
          ) : (
            <div className="mb-2">
              <label className="mb-2 text-input-label">
                {intl.formatMessage({ id: "image" })}{" "}
                {intl.formatMessage({ id: "gameImageInfo" })}{" "}
              </label>
              {imagePreview && imagePreview !== "" && (
                <div
                  style={{ maxHeight: "200px" }}
                  className="mb-2 position-relative"
                >
                  <img
                    src={imagePreview}
                    style={{ maxHeight: "200px" }}
                    onError={() => setImagePreview(undefined)}
                  />

                  <i
                    className="position-absolute bi bi-x-circle-fill text-danger fs-5 cursor-pointer"
                    onClick={() => {
                      setImagePreview(undefined);
                      setFieldValue("image", undefined);
                      fileInputRef.current.value = "";
                    }}
                    style={{ top: "0px", left: "5px" }}
                  />
                </div>
              )}

              <input
                type="file"
                className="form-control text-font2"
                placeholder={intl.formatMessage({ id: "image" })}
                autoComplete="off"
                accept="image/webp"
                onChange={handleFileChange}
                ref={fileInputRef}
                onClick={() => setFieldTouched("image", true)}
              />
              <ErrorContainer
                isShow={touched?.image && errors?.image}
                errorMsg={errors?.image}
              />
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
